import {
  BlockStateSourceError,
  type BlockStateOp,
} from "@cohesive/ui-components";
import createDebug from "debug";
import {
  BlockStateTokenRefused,
  blockStateTokens,
  RENEW_UNDER_MS,
} from "./blockStateTokens";
import {
  blockStateUrl,
  toWire,
  type WireError,
  type WireRow,
} from "./blockStateWorker";

const debug = createDebug("cohesive:block-state-hub");

const PING_MS = 30_000;
const SILENCE_MS = 45_000;
const BACKOFF_MIN_MS = 250;
const BACKOFF_MAX_MS = 10_000;
const SERVER_CLOSE_JITTER_MS = 1_000;
const HIDDEN_CLOSE_MS = 30_000;

const CLOSE_EXPIRED = 4401;
const CLOSE_FORBIDDEN = 4403;
const CLOSE_UNAVAILABLE = 1011;

/** A block the tab shows, subscribed through the hub while it is held. */
export interface HubMember {
  readonly id: string;
  /** The block as it stands, after every subscribe: on each connect and
   *  each token renewal. */
  snapshot(version: number, rows: WireRow[]): void;
  /** Rows other writers changed. */
  change(rows: WireRow[]): void;
  /** The hub cannot be reached, or the API refused a token: the data is
   *  unavailable until the next snapshot. */
  unavailable(): void;
}

type Frame =
  | {
      type: "snapshot" | "change";
      file: string;
      version: number;
      rows: WireRow[];
    }
  | { type: "refused"; file: string; code: number; reason?: string }
  | { type: "ack"; id: number; version: number; rows: WireRow[] }
  | ({ type: "nack"; id: number; rows?: WireRow[] } & WireError);

type Pending = {
  file: string;
  resolve: (rows: WireRow[]) => void;
  reject: (err: BlockStateSourceError) => void;
};

type Sub = {
  member: HubMember;
  /** Renewal, or a retry after the token call failed. */
  timer: ReturnType<typeof setTimeout> | null;
  /** A 4401 is answered with one fresh token; a second one ends it. */
  renewedOnRefusal: boolean;
  /** Writable: true from its snapshot until the socket resets. */
  ready: boolean;
};

/**
 * The tab's one socket to the block-state Worker. Every held block
 * subscribes on it with its own token; writes travel on it in order with
 * the changes. An unanswered write fails when it closes and is never
 * resent, so an increment cannot count twice.
 */
export class BlockStateHub {
  private subs = new Map<string, Sub>();
  private ws: WebSocket | null = null;
  private live = false;
  private attempt = 0;
  private nextId = 1;
  private pending = new Map<number, Pending>();
  private lastHeard = 0;
  private lastPing = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private hidden = false;
  /** The capture page reads once over HTTPS and opens nothing. */
  disabled = false;

  constructor() {
    if (typeof document === "undefined") return;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.hiddenTimer ??= setTimeout(() => {
          this.hiddenTimer = null;
          this.hidden = true;
          this.drop();
        }, HIDDEN_CLOSE_MS);
        return;
      }
      if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
      this.hidden = false;
      this.ensureOpen();
    });
  }

  get isLive(): boolean {
    return this.live;
  }

  add(member: HubMember): void {
    if (this.disabled || this.subs.has(member.id)) return;
    const sub: Sub = {
      member,
      timer: null,
      renewedOnRefusal: false,
      ready: false,
    };
    this.subs.set(member.id, sub);
    if (this.live) void this.subscribe(sub);
    else this.ensureOpen();
  }

  remove(id: string): void {
    const sub = this.subs.get(id);
    if (!sub) return;
    if (sub.timer) clearTimeout(sub.timer);
    this.subs.delete(id);
    if (this.subs.size === 0) {
      this.drop();
      return;
    }
    if (this.live) this.send({ type: "unsubscribe", files: [id] });
  }

  write(id: string, ops: readonly BlockStateOp[]): Promise<WireRow[]> {
    if (!this.subs.get(id)?.ready) {
      return Promise.reject(
        new BlockStateSourceError("failed", "The write was not saved."),
      );
    }
    const reqId = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(reqId, { file: id, resolve, reject });
      this.send({ type: "write", id: reqId, file: id, ops: ops.map(toWire) });
    });
  }

  private ensureOpen(): void {
    if (this.ws || this.retry || this.hidden || this.subs.size === 0) return;
    this.connect();
  }

  private connect(): void {
    const url = blockStateUrl("/v1/hub");
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(url);
    this.ws = ws;
    let opened = false;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      opened = this.live = true;
      this.startHeartbeat();
      for (const sub of this.subs.values()) void this.subscribe(sub);
    };
    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      this.lastHeard = performance.now();
      if (event.data === "pong" || typeof event.data !== "string") return;
      let frame: Frame;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }
      this.onFrame(frame);
    };
    ws.onclose = (event) => {
      if (this.ws !== ws) return;
      this.reset();
      debug("closed %d %s", event.code, event.reason);
      // The Worker refuses this origin: retrying cannot help.
      if (event.code === CLOSE_FORBIDDEN) {
        for (const sub of this.subs.values()) sub.member.unavailable();
        this.subs.clear();
        return;
      }
      if (!opened) {
        for (const sub of this.subs.values()) sub.member.unavailable();
      }
      if (this.subs.size > 0 && !this.hidden) this.scheduleRetry(opened);
    };
  }

  private async subscribe(sub: Sub, renew = false): Promise<void> {
    const { id } = sub.member;
    if (sub.timer) clearTimeout(sub.timer);
    sub.timer = null;
    let token;
    try {
      token = await blockStateTokens.get(id, renew);
    } catch (err) {
      if (this.subs.get(id) !== sub) return;
      sub.member.unavailable();
      if (err instanceof BlockStateTokenRefused) {
        this.forget(id);
        return;
      }
      sub.timer = setTimeout(() => void this.subscribe(sub), BACKOFF_MAX_MS);
      return;
    }
    if (this.subs.get(id) !== sub || !this.live) return;
    this.send({
      type: "subscribe",
      blocks: [{ file: id, token: token.token }],
    });
    // Renewal is a subscribe with the fresh token; the hub refuses a block
    // whose token has run out only when it next has something for it.
    sub.timer = setTimeout(
      () => void this.subscribe(sub),
      Math.max(0, token.expiresAt - RENEW_UNDER_MS - performance.now()),
    );
  }

  private onFrame(frame: Frame): void {
    switch (frame.type) {
      case "snapshot": {
        const sub = this.subs.get(frame.file);
        if (!sub) return;
        this.attempt = 0;
        sub.renewedOnRefusal = false;
        sub.ready = true;
        sub.member.snapshot(frame.version, frame.rows);
        return;
      }
      case "change":
        this.subs.get(frame.file)?.member.change(frame.rows);
        return;
      case "refused": {
        const sub = this.subs.get(frame.file);
        if (!sub) return;
        debug("%s refused %d %s", frame.file, frame.code, frame.reason);
        if (frame.code === CLOSE_EXPIRED && !sub.renewedOnRefusal) {
          sub.renewedOnRefusal = true;
          void this.subscribe(sub, true);
          return;
        }
        if (sub.timer) clearTimeout(sub.timer);
        if (frame.code === CLOSE_UNAVAILABLE) {
          sub.ready = false;
          sub.member.unavailable();
          sub.timer = setTimeout(
            () => void this.subscribe(sub),
            BACKOFF_MAX_MS,
          );
          return;
        }
        this.forget(frame.file);
        sub.member.unavailable();
        return;
      }
      case "ack":
      case "nack": {
        const p = this.pending.get(frame.id);
        if (!p) return;
        this.pending.delete(frame.id);
        if (frame.type === "ack") {
          p.resolve(frame.rows);
          return;
        }
        if (frame.rows?.length)
          this.subs.get(p.file)?.member.change(frame.rows);
        p.reject(
          new BlockStateSourceError(
            frame.code,
            frame.message,
            frame.retry_after_ms,
          ),
        );
        return;
      }
    }
  }

  private startHeartbeat(): void {
    this.lastHeard = this.lastPing = performance.now();
    this.heartbeat = setInterval(() => {
      const now = performance.now();
      if (now - this.lastHeard >= SILENCE_MS) {
        debug("silent, reconnecting");
        this.drop();
        this.connect();
        return;
      }
      if (now - this.lastPing >= PING_MS) {
        this.lastPing = now;
        this.ws?.send("ping");
      }
    }, PING_MS / 2);
  }

  private scheduleRetry(serverClosed: boolean): void {
    const cap = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.attempt);
    this.attempt += 1;
    const delay =
      BACKOFF_MIN_MS +
      Math.random() * (cap - BACKOFF_MIN_MS) +
      (serverClosed ? Math.random() * SERVER_CLOSE_JITTER_MS : 0);
    this.retry = setTimeout(() => {
      this.retry = null;
      this.ensureOpen();
    }, delay);
  }

  private forget(id: string): void {
    this.subs.delete(id);
    if (this.subs.size === 0) this.drop();
  }

  private send(frame: unknown): void {
    this.ws?.send(JSON.stringify(frame));
  }

  /** Close our side; the stale socket's events are ignored from here. */
  private drop(): void {
    const ws = this.ws;
    this.reset();
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    try {
      ws?.close(1000);
    } catch {
      // Already closing.
    }
  }

  private reset(): void {
    this.ws = null;
    this.live = false;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    for (const sub of this.subs.values()) {
      if (sub.timer) clearTimeout(sub.timer);
      sub.timer = null;
      sub.ready = false;
    }
    const failed = new BlockStateSourceError(
      "failed",
      "The connection closed before the write was answered.",
    );
    for (const p of this.pending.values()) p.reject(failed);
    this.pending.clear();
  }
}

export const blockStateHub = new BlockStateHub();
